import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Chip, Drawer, Divider, Button,
  TextField, CircularProgress, IconButton, Tooltip, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { crushingWasteLotService } from '../services/api';
import { orderRefFromRow } from '../utils/orderLabel';

const COLUMNS = [
  { key: 'new', label: 'Нові', color: '#1565c0', bg: '#e3f2fd' },
  { key: 'in_progress', label: 'В процесі', color: '#e65100', bg: '#fff3e0' },
  { key: 'done', label: 'Виконано', color: '#2e7d32', bg: '#e8f5e9' },
];

function fmtDate(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString('uk-UA', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return val;
  }
}

function CrushingCard({ item, colColor, colBg, onClick }) {
  const planned = item.quantity_initial ?? item.quantity_kg;
  return (
    <Paper
      elevation={2}
      onClick={() => onClick(item)}
      sx={{
        p: 1.5, mb: 1.5, cursor: 'pointer', borderLeft: `4px solid ${colColor}`,
        '&:hover': { boxShadow: 4 },
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold" sx={{ whiteSpace: 'normal' }}>
        {item.peremol_name || 'без складу'}
      </Typography>
      <Typography variant="body2" mt={0.5}>
        {item.production_order_id
          ? <>Замовлення: <b>{orderRefFromRow(item)}</b></>
          : <>Джерело: <b>Склад виробництва</b></>}
      </Typography>
      {item.product_article || item.product_name ? (
        <Typography variant="caption" color="text.secondary" display="block">
          Джерело: {item.product_article ? `${item.product_article} — ` : ''}{item.product_name}
        </Typography>
      ) : null}
      <Typography variant="body2" mt={0.5}>
        План / залишок: <b>{planned}</b> кг
      </Typography>
      {item.input_weight_kg != null && (
        <Typography variant="body2">Вхід: <b>{item.input_weight_kg}</b> кг</Typography>
      )}
      {item.output_weight_kg != null && (
        <Typography variant="body2">
          Вихід: <b>{item.output_weight_kg}</b> кг
          {item.loss_kg != null ? ` · втрата ${item.loss_kg} кг` : ''}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
        Створено: {fmtDate(item.created_at)}
      </Typography>
      <Box mt={0.5}>
        <Chip
          label={item.board_status_display || item.board_status}
          size="small"
          sx={{ bgcolor: colBg, color: colColor, fontWeight: 600 }}
        />
      </Box>
    </Paper>
  );
}

export default function CrushingPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [inputWeight, setInputWeight] = useState('');
  const [outputWeight, setOutputWeight] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crushingWasteLotService.getAll({ kanban: true, show_all: true });
      setItems(res.data.results || res.data || []);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byColumn = useMemo(() => {
    const map = { new: [], in_progress: [], done: [] };
    for (const it of items) {
      const key = it.board_status || 'new';
      if (map[key]) map[key].push(it);
      else map.new.push(it);
    }
    // Нові/в процесі — старіші зверху (черга); виконано — новіші зверху
    map.new.sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));
    map.in_progress.sort((a, b) => String(a.started_at || '').localeCompare(String(b.started_at || '')));
    map.done.sort((a, b) => String(b.completed_at || '').localeCompare(String(a.completed_at || '')));
    return map;
  }, [items]);

  function openDrawer(item) {
    setSelected(item);
    setError('');
    setInputWeight(
      item.input_weight_kg != null
        ? String(item.input_weight_kg)
        : String(item.quantity_kg ?? item.quantity_initial ?? '')
    );
    setOutputWeight(item.output_weight_kg != null ? String(item.output_weight_kg) : '');
    setDrawerOpen(true);
  }

  async function handleStart() {
    if (!selected) return;
    const qty = parseFloat(String(inputWeight).replace(',', '.'));
    if (!Number.isFinite(qty) || qty <= 0) {
      setError('Вкажіть фактичну вхідну вагу (кг)');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await crushingWasteLotService.startCrushing(selected.id, { input_weight_kg: qty });
      setSelected(res.data);
      setDrawerOpen(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.input_weight_kg || e?.response?.data?.detail || e?.response?.data?.error || 'Помилка старту');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleComplete() {
    if (!selected) return;
    const qty = parseFloat(String(outputWeight).replace(',', '.'));
    if (!Number.isFinite(qty) || qty < 0) {
      setError('Вкажіть фактичну вихідну вагу (кг)');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await crushingWasteLotService.completeCrushing(selected.id, { output_weight_kg: qty });
      setDrawerOpen(false);
      await load();
    } catch (e) {
      const d = e?.response?.data;
      setError(d?.output_weight_kg || d?.detail || d?.error || 'Помилка завершення');
    } finally {
      setSubmitting(false);
    }
  }

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || 'null'); } catch { return null; }
  })();
  const perms = currentUser?.effective_permissions || {};
  if (perms['menu.crushing'] === false) {
    return (
      <Box p={2}>
        <Typography>Немає доступу до «Дроблення» (право menu.crushing).</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: 'calc(100vh - 112px)', display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Typography variant="h5" sx={{ flex: 1 }}>Дроблення</Typography>
        <Tooltip title="Оновити">
          <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 920 }}>
        Відновлювальні відходи з виробництва потрапляють у колонку «Нові» з назвою «сировина + барвник».
        Почніть дроблення з підтвердженням вхідної ваги, завершіть із обовʼязковою вихідною вагою —
        після цього зʼявиться надходження на Складі перемолів; залишок зʼявиться лише після «Прийняти».
        Різниця вхід/вихід списується як технологічна втрата.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <Box
          sx={{
            flex: 1, minHeight: 0, display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, overflow: 'hidden',
          }}
        >
          {COLUMNS.map((col) => (
            <Paper
              key={col.key}
              variant="outlined"
              sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: '#fafafa' }}
            >
              <Box sx={{ px: 2, py: 1.25, bgcolor: col.bg, borderBottom: `2px solid ${col.color}` }}>
                <Typography fontWeight={700} sx={{ color: col.color }}>
                  {col.label} ({byColumn[col.key]?.length || 0})
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, overflowY: 'auto', flex: 1 }}>
                {(byColumn[col.key] || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
                    Порожньо
                  </Typography>
                ) : (
                  byColumn[col.key].map((item) => (
                    <CrushingCard
                      key={item.id}
                      item={item}
                      colColor={col.color}
                      colBg={col.bg}
                      onClick={openDrawer}
                    />
                  ))
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <Drawer anchor="right" open={drawerOpen} onClose={() => !submitting && setDrawerOpen(false)}>
        <Box sx={{ width: { xs: '100vw', sm: 420 }, p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="h6" sx={{ flex: 1 }}>Партія дроблення</Typography>
            <IconButton onClick={() => setDrawerOpen(false)} disabled={submitting}><CloseIcon /></IconButton>
          </Box>
          {selected && (
            <>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                {selected.peremol_name}
              </Typography>
              <Typography variant="body2">
                {selected.production_order_id
                  ? `Замовлення: ${orderRefFromRow(selected)}`
                  : 'Джерело: Склад виробництва'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {selected.product_article ? `${selected.product_article} — ` : ''}{selected.product_name}
              </Typography>
              <Typography variant="body2">План відходів: {selected.quantity_initial} кг</Typography>
              <Typography variant="body2" gutterBottom>
                Статус: {selected.board_status_display || selected.board_status}
              </Typography>
              <Divider sx={{ my: 1.5 }} />

              {error && <Alert severity="error" sx={{ mb: 2 }}>{String(error)}</Alert>}

              {selected.board_status === 'new' && (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Підтвердіть або скоригуйте <b>вхідну вагу</b>, яку засипаєте в дробарку.
                  </Typography>
                  <TextField
                    label="Вхідна вага (кг)"
                    type="number"
                    fullWidth
                    value={inputWeight}
                    onChange={(e) => setInputWeight(e.target.value)}
                    inputProps={{ min: 0.001, step: 'any' }}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<PlayArrowIcon />}
                    disabled={submitting}
                    onClick={handleStart}
                    fullWidth
                  >
                    {submitting ? <CircularProgress size={22} /> : 'Почати дроблення'}
                  </Button>
                </>
              )}

              {selected.board_status === 'in_progress' && (
                <>
                  <Typography variant="body2" gutterBottom>
                    Вхід зафіксовано: <b>{selected.input_weight_kg}</b> кг · старт {fmtDate(selected.started_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Вкажіть <b>фактичну вихідну вагу</b> після дроблення. Різниця стане технологічною втратою;
                    вихідна вага піде в «Надходження» Складу перемолів і потрапить у залишок після «Прийняти».
                  </Typography>
                  <TextField
                    label="Вихідна вага (кг) *"
                    type="number"
                    required
                    fullWidth
                    value={outputWeight}
                    onChange={(e) => setOutputWeight(e.target.value)}
                    inputProps={{ min: 0, step: 'any' }}
                    sx={{ mb: 2 }}
                  />
                  {outputWeight !== '' && Number.isFinite(parseFloat(outputWeight)) && selected.input_weight_kg != null && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Технологічна втрата:{' '}
                      <b>
                        {Math.max(0, round3(selected.input_weight_kg - parseFloat(String(outputWeight).replace(',', '.'))))}
                      </b>{' '}
                      кг
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    disabled={submitting}
                    onClick={handleComplete}
                    fullWidth
                  >
                    {submitting ? <CircularProgress size={22} /> : 'Завершити'}
                  </Button>
                </>
              )}

              {selected.board_status === 'done' && (
                <Alert severity={selected.confirmed_at ? 'success' : 'info'}>
                  Виконано. Вхід {selected.input_weight_kg} кг → вихід {selected.output_weight_kg} кг
                  {selected.loss_kg != null ? ` (втрата ${selected.loss_kg} кг)` : ''}.
                  {selected.confirmed_at
                    ? ' Прийнято на Склад перемолів.'
                    : ' Очікує «Прийняти» у вкладці «Надходження» на Складі перемолів.'}
                </Alert>
              )}
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}

function round3(n) {
  return Math.round(n * 1000) / 1000;
}
